import { create } from "zustand";
import { listSavedTrends, type SavedTrendSummary } from "../data/savedTrends";

/**
 * The signed-in user's saved trends, for the sidebar's list. Loaded once per
 * user and kept in step locally as trends are saved and deleted, most
 * recently changed first.
 */
interface SavedTrendsState {
  /** Whose list this is; another user's sign-in starts it over. */
  userId: string | null;
  trends: SavedTrendSummary[];
  status: "idle" | "loading" | "ready" | "error";
  /** Loads `userId`'s list, unless it's loading or loaded already. `force` loads it again. */
  load: (userId: string, options?: { force?: boolean }) => Promise<void>;
  /** A trend was created or changed: it goes to the top. */
  upsert: (trend: SavedTrendSummary) => void;
  remove: (id: string) => void;
  reset: () => void;
}

export const useSavedTrendsStore = create<SavedTrendsState>()((set, get) => ({
  userId: null,
  trends: [],
  status: "idle",
  load: async (userId, options) => {
    const { status, userId: owner } = get();
    if (owner === userId && (status === "loading" || (status === "ready" && !options?.force))) return;
    set({ userId, status: "loading", ...(owner === userId ? {} : { trends: [] }) });
    try {
      const trends = await listSavedTrends();
      if (get().userId === userId) set({ trends, status: "ready" });
    } catch (error) {
      console.error("Failed to load saved trends:", error);
      if (get().userId === userId) set({ status: "error" });
    }
  },
  upsert: (trend) => set((s) => ({ trends: [trend, ...s.trends.filter((t) => t.id !== trend.id)] })),
  remove: (id) => set((s) => ({ trends: s.trends.filter((t) => t.id !== id) })),
  reset: () => set({ userId: null, trends: [], status: "idle" }),
}));
